import { BankOutlined } from '@ant-design/icons';
import { Button, Card, Space, Tag, Typography } from 'antd';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { authStore, useAdminAuthState } from '../../stores/auth';

type EnterpriseContextHeaderProps = Readonly<{
  organizationId?: string;
  organizationName?: string;
  resourceId?: string;
}>;

const enterpriseContextDestinations = [
  { label: '企业详情', path: '/enterprise/organizations', permission: 'enterprise:organization:read' },
  { label: '服务申请', path: '/enterprise/applications', permission: 'enterprise:application:read' },
  { label: '号码资源', path: '/enterprise/numbers', permission: 'enterprise:number:read' },
  { label: '服务履约', path: '/enterprise-service', permission: 'enterprise:fulfillment:read' },
  { label: '客服工单', path: '/enterprise/support', permission: 'enterprise:support:read' },
  { label: '坐席团队', path: '/enterprise/agents', permission: 'enterprise:agent_team:read' },
  { label: '企业通信', path: '/enterprise/communications', permission: 'enterprise:channel:read' },
  { label: '企业渠道', path: '/enterprise/channels', permission: 'enterprise:channel:read' },
  { label: '账单续费', path: '/enterprise/billing', permission: 'enterprise:billing:read' },
  { label: '审计风控', path: '/enterprise/audit', permission: 'enterprise:audit:read' },
] as const;

export function enterpriseContextPath(path: string, organizationId?: string, resourceId?: string) {
  const [pathname, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  if (organizationId) params.set('organizationId', organizationId);
  if (resourceId) params.set('resourceId', resourceId);
  const suffix = params.toString();
  return `${pathname}${suffix ? `?${suffix}` : ''}`;
}

export function EnterpriseContextHeader(props: EnterpriseContextHeaderProps) {
  useAdminAuthState();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const detailMatch = matchPath('/enterprise/organizations/:id', location.pathname);
  const organizationId = props.organizationId ?? params.get('organizationId') ?? detailMatch?.params.id ?? undefined;
  const resourceId = props.resourceId ?? params.get('resourceId') ?? undefined;
  const organizationName = props.organizationName ?? params.get('organizationName') ?? undefined;

  const destinationPath = (path: string) => path === '/enterprise/organizations' && organizationId
    ? enterpriseContextPath(`/enterprise/organizations/${encodeURIComponent(organizationId)}`, organizationId)
    : enterpriseContextPath(path, organizationId);
  const visibleDestinations = enterpriseContextDestinations.filter((destination) =>
    authStore.canPermissions([destination.permission]));

  return (
    <Card size="small" style={{ marginBottom: 16 }} styles={{ body: { padding: '14px 16px' } }}>
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <Space wrap size={8}>
          <BankOutlined style={{ color: '#1677ff' }} />
          <Typography.Text strong>{organizationId ? '当前企业' : '全平台企业视图'}</Typography.Text>
          {organizationName ? <Typography.Text>{organizationName}</Typography.Text> : null}
          {organizationId ? <Tag color="blue">企业编号 {organizationId}</Tag> : <Typography.Text type="secondary">选择企业后，跨栏目会自动保留企业上下文。</Typography.Text>}
          {resourceId ? <Tag>当前资源 {resourceId}</Tag> : null}
        </Space>
        <Space wrap size={[8, 8]}>
          {visibleDestinations.map((destination) => (
            <Button
              key={destination.path}
              size="small"
              type={location.pathname === destination.path || location.pathname.startsWith(`${destination.path}/`) ? 'primary' : 'default'}
              onClick={() => navigate(destinationPath(destination.path))}
            >
              {destination.label}
            </Button>
          ))}
        </Space>
      </Space>
    </Card>
  );
}
